'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  useGetFolderItemsApi,
  FolderItemsResponseType,
  GetFolderItemsContentResponseType,
  GetFolderItemsFolderResponseType,
} from '@/shared/apis/folder/get-folder-items';
import { useBookmarkSet } from '@/shared/hooks/useBookmarkSet';
import { DtoContentType, DtoFolderItemType } from '@/shared/constants';

/**
 * FolderType enum (백엔드 정의와 동일)
 * ROOT(0), PROJECT(1), USER_ASSET(2), SCRIPT(3), INSIGHT(4), CUSTOM(99)
 */
export enum FolderType {
  ROOT = 0,
  PROJECT = 1,
  USER_ASSET = 2,
  SCRIPT = 3,
  INSIGHT = 4,
  CUSTOM = 99,
}

/** 폴더 경로 정보 */
export interface PathInfo {
  id: number;
  name: string;
  folderType?: FolderType;
}

/** useAssetPicker 반환 타입 */
export interface UseAssetPickerReturn {
  // 상태
  currentFolderId: number;
  folderPath: PathInfo[];
  items: FolderItemsResponseType[];
  isLoading: boolean;

  // 선택
  selectedItems: Map<number, GetFolderItemsContentResponseType>;
  toggleSelect: (item: GetFolderItemsContentResponseType) => void;
  clearSelection: () => void;
  selectAll: () => void;

  // 네비게이션
  navigateToFolder: (folder: GetFolderItemsFolderResponseType) => void;
  navigateBack: () => void;
  navigateToPathIndex: (index: number) => void;

  // 필터
  filter: DtoContentType | null;
  setFilter: (filter: DtoContentType | null) => void;
  searchKeyword: string;
  setSearchKeyword: (keyword: string) => void;
  bookmarkOnly: boolean;
  setBookmarkOnly: (value: boolean) => void;

  // 유틸
  contentItems: GetFolderItemsContentResponseType[];
  isBookmarked: (itemId: number) => boolean;
}

/**
 * 에셋 선택 모달의 상태를 관리하는 훅
 * - 폴더 탐색
 * - 다중 선택
 * - 필터/검색
 * - 북마크 필터
 */
export function useAssetPicker(
  startFolderId: number,
  startFolderType: FolderType = FolderType.ROOT,
  startFolderName: string = 'folder.type.root',
  /** 허용할 콘텐츠 타입 목록 (지정하지 않으면 모두 허용) */
  allowedContentTypes?: DtoContentType[],
  /** 최대 선택 가능 개수 (지정하지 않으면 무제한) */
  maxSelections?: number,
): UseAssetPickerReturn {
  // 폴더 탐색 상태
  const [currentFolderId, setCurrentFolderId] = useState(startFolderId);
  const [folderPath, setFolderPath] = useState<PathInfo[]>([
    { id: startFolderId, name: startFolderName, folderType: startFolderType },
  ]);

  // startFolderId가 변경되면 상태 리셋
  useEffect(() => {
    setCurrentFolderId(startFolderId);
    setFolderPath([
      { id: startFolderId, name: startFolderName, folderType: startFolderType },
    ]);
  }, [startFolderId, startFolderType, startFolderName]);

  // 선택 상태
  const [selectedItems, setSelectedItems] = useState<
    Map<number, GetFolderItemsContentResponseType>
  >(new Map());

  // 필터 상태
  const [filter, setFilter] = useState<DtoContentType | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [bookmarkOnly, setBookmarkOnly] = useState(false);

  // 북마크 Set
  const bookmarkSet = useBookmarkSet();

  // 북마크 여부 확인 함수 (외부 사용용)
  const isBookmarked = useCallback(
    (itemId: number): boolean => {
      // 콘텐츠 타입은 DtoFolderItemType.content = 1
      const bookmarkKey = `${DtoFolderItemType.content}-${itemId}`;
      return bookmarkSet.has(bookmarkKey);
    },
    [bookmarkSet],
  );

  // API 호출
  const { data, isLoading } = useGetFolderItemsApi(
    {
      id: currentFolderId,
      project: false,
      sort: 'createdAt,desc',
    },
    {
      enabled: currentFolderId > 0,
    },
  );

  // 폴더 타입 필터링 (USER_ASSET, CUSTOM만 표시, PROJECT/SCRIPT/INSIGHT 숨김)
  const filteredItems = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((item) => {
      // 콘텐츠 아이템
      if (item.type === DtoFolderItemType.content) {
        const contentItem = item as GetFolderItemsContentResponseType;

        // 허용된 콘텐츠 타입 필터 (allowedContentTypes가 지정된 경우)
        if (
          allowedContentTypes &&
          !allowedContentTypes.includes(contentItem.contentType)
        ) {
          return false;
        }

        // 북마크 필터 (my-space와 동일한 방식)
        if (bookmarkOnly) {
          const bookmarkKey = `${item.type}-${item.itemId}`;
          if (!bookmarkSet.has(bookmarkKey)) {
            return false;
          }
        }

        // 콘텐츠 타입 필터
        if (filter !== null && contentItem.contentType !== filter) {
          return false;
        }

        // 검색 필터
        if (
          searchKeyword &&
          !contentItem.name.toLowerCase().includes(searchKeyword.toLowerCase())
        ) {
          return false;
        }

        return true;
      }

      // 폴더 아이템 (USER_ASSET, CUSTOM만 표시)
      if (item.type === DtoFolderItemType.folder) {
        const folderItem = item as GetFolderItemsFolderResponseType;
        const allowedTypes = [FolderType.USER_ASSET, FolderType.CUSTOM];
        return allowedTypes.includes(folderItem.folderType as FolderType);
      }

      // 프로젝트는 숨김
      return false;
    });
  }, [
    data?.data,
    filter,
    searchKeyword,
    bookmarkOnly,
    bookmarkSet,
    allowedContentTypes,
  ]);

  // 콘텐츠 아이템만 추출 (선택 가능한 항목)
  const contentItems = useMemo(() => {
    return filteredItems.filter(
      (item) => item.type === DtoFolderItemType.content,
    ) as GetFolderItemsContentResponseType[];
  }, [filteredItems]);

  // 선택 토글
  const toggleSelect = useCallback(
    (item: GetFolderItemsContentResponseType) => {
      setSelectedItems((prev) => {
        const next = new Map(prev);
        if (next.has(item.itemId)) {
          next.delete(item.itemId);
        } else {
          // maxSelections가 1이면 기존 선택 제거 후 새로 선택
          if (maxSelections === 1) {
            next.clear();
            next.set(item.itemId, item);
          } else if (maxSelections && next.size >= maxSelections) {
            // 최대 개수 도달 시 추가 불가
            return prev;
          } else {
            next.set(item.itemId, item);
          }
        }
        return next;
      });
    },
    [maxSelections],
  );

  // 전체 선택 해제
  const clearSelection = useCallback(() => {
    setSelectedItems(new Map());
  }, []);

  // 전체 선택 (현재 폴더의 콘텐츠만)
  const selectAll = useCallback(() => {
    const newSelection = new Map<number, GetFolderItemsContentResponseType>();
    contentItems.forEach((item) => {
      newSelection.set(item.itemId, item);
    });
    setSelectedItems(newSelection);
  }, [contentItems]);

  // 폴더로 이동
  const navigateToFolder = useCallback(
    (folder: GetFolderItemsFolderResponseType) => {
      setCurrentFolderId(folder.itemId);
      setFolderPath((prev) => [
        ...prev,
        {
          id: folder.itemId,
          name: folder.name,
          folderType: folder.folderType as FolderType,
        },
      ]);
    },
    [],
  );

  // 뒤로 가기
  const navigateBack = useCallback(() => {
    if (folderPath.length > 1) {
      const newPath = folderPath.slice(0, -1);
      setFolderPath(newPath);
      setCurrentFolderId(newPath[newPath.length - 1]!.id);
    }
  }, [folderPath]);

  // 브레드크럼에서 특정 경로로 이동
  const navigateToPathIndex = useCallback(
    (index: number) => {
      if (index < folderPath.length) {
        const newPath = folderPath.slice(0, index + 1);
        setFolderPath(newPath);
        setCurrentFolderId(newPath[newPath.length - 1]!.id);
      }
    },
    [folderPath],
  );

  return {
    // 상태
    currentFolderId,
    folderPath,
    items: filteredItems,
    isLoading,

    // 선택
    selectedItems,
    toggleSelect,
    clearSelection,
    selectAll,

    // 네비게이션
    navigateToFolder,
    navigateBack,
    navigateToPathIndex,

    // 필터
    filter,
    setFilter,
    searchKeyword,
    setSearchKeyword,
    bookmarkOnly,
    setBookmarkOnly,

    // 유틸
    contentItems,
    isBookmarked,
  };
}

export default useAssetPicker;
